import { useParams } from '@tanstack/react-router';

import { AttachmentModelRender } from './attachment-model-render.js';
import { parseBoundingBoxAttribute } from './bounding-box-attribute.js';
import { ProjectModelRender } from './project-model-render.js';

export interface ModelRenderMarkdownItemProps {
	/** Directive content: a display label used as a fallback title. */
	label?: string;
	/** Renders a saved block-diagram attachment (compiled at view time). */
	attachmentId?: string;
	/**
	 * Design to display. With `attachmentId`, selects within the compiled
	 * diagram; without it, names a design of the current revision's project model.
	 */
	designId?: string;
	/** Initial zoom box as `"minx,miny,maxx,maxy"` in model coordinates. */
	bbox?: string;
}

/**
 * Inline project-model render for markdown content (agent workbench, notes),
 * dispatched by source: a saved block-diagram attachment when `attachmentId`
 * is given, otherwise a design of the current revision's real project model
 * (requiring an org/project/revision route context). Degrades to the plain
 * label when neither source is available.
 *
 * @param props - Component props.
 */
export function ModelRenderMarkdownItem({ label, attachmentId, designId, bbox }: ModelRenderMarkdownItemProps) {
	const { orgName, projectId, revisionId } = useParams({ strict: false });
	const parsedBbox = parseBoundingBoxAttribute(bbox);

	if (attachmentId !== undefined && attachmentId !== '') {
		return <AttachmentModelRender attachmentId={attachmentId} label={label} designId={designId} bbox={parsedBbox} />;
	}

	if (orgName !== undefined && projectId !== undefined && revisionId !== undefined) {
		return (
			<ProjectModelRender
				orgName={orgName}
				projectId={projectId}
				revisionId={revisionId}
				designId={designId}
				bbox={parsedBbox}
				label={label}
			/>
		);
	}

	return label !== undefined && label !== '' ? <span>{label}</span> : null;
}
